import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  FiArrowDown,
  FiArrowUp,
  FiEdit2,
  FiTrash,
  FiPlus,
  FiList,
  FiMinus,
} from "react-icons/fi";
import swal from "@sweetalert/with-react";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import BigLoader from "../../components/BigLoader";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";

export default function DeepReadBook() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deepRead, setDeepRead] = useState(null);
  const [books, setBooks] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [saveArranged, setSaveArranged] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [ringsByBook, setRingsByBook] = useState({});

  const fetchData = () => {
    setLoading(true);
    service
      .get(`/api/deep-read/${id}`)
      .then((res) => {
        setDeepRead(res.data);
        setBooks(res.data?.books || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 404) {
          history.replace("/deep-read");
        }
      });
  };

  const onDelete = (bookId) => {
    swal({
      title: "Anda yakin?",
      text: "Anda akan menghapus buku ini",
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
    }).then((value) => {
      if (value) {
        setLoadingDelete(true);
        service
          .delete(`/api/deep-read/${id}/books/${bookId}`)
          .then(() => {
            swal("Berhasil", "Buku telah dihapus", "success");
            fetchData();
            setLoadingDelete(false);
          })
          .catch(() => setLoadingDelete(false));
      }
    });
  };

  const fetchRings = (bookId) => {
    service
      .get(`/api/deep-read/${id}/books/${bookId}`)
      .then((res) => {
        setRingsByBook((prev) => ({ ...prev, [bookId]: res.data?.rings || [] }));
      })
      .catch(() => {
        setRingsByBook((prev) => ({ ...prev, [bookId]: [] }));
      });
  };

  const changePosition = (direction, index) => {
    const next = [...books];
    const swapWith = direction === "up" ? index - 1 : index + 1;
    next[swapWith] = next.splice(index, 1, next[swapWith])[0];
    setBooks(next.map((b, idx) => ({ ...b, position: idx + 1 })));
    setSaveArranged(true);
  };

  const saveArrangedBooks = () => {
    setSavingOrder(true);
    Promise.all(
      books.map((book, idx) =>
        service.put(`/api/deep-read/${id}/books/${book.id}`, {
          position: idx + 1,
        })
      )
    )
      .then(() => {
      setSaveArranged(false);
      fetchData();
    })
      .finally(() => setSavingOrder(false));
  };

  const toggleExpand = (bookId) => {
    setExpanded((prev) => {
      const next = { ...prev, [bookId]: !prev[bookId] };
      return next;
    });
    if (!ringsByBook[bookId]) {
      fetchRings(bookId);
    }
  };

  const reorderRing = (bookId, direction, index) => {
    const rings = ringsByBook[bookId] || [];
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= rings.length) return;
    const ordered = [...rings];
    ordered[swapWith] = ordered.splice(index, 1, ordered[swapWith])[0];
    const withPositions = ordered.map((r, idx) => ({ ...r, position: idx + 1 }));
    setSavingOrder(true);
    Promise.all(
      withPositions.map((ring, idx) =>
        service.put(`/api/deep-read/rings/${ring.id}`, { position: idx + 1 })
      )
    )
      .then(() => {
        setRingsByBook((prev) => ({ ...prev, [bookId]: withPositions }));
        setBooks((prev) =>
          prev.map((b) =>
            b.id === bookId ? { ...b, rings_count: withPositions.length } : b
          )
        );
      })
      .finally(() => setSavingOrder(false));
  };

  const deleteRing = (bookId, ringId) => {
    swal({
      title: "Anda yakin?",
      text: "Anda akan menghapus ring ini",
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
    }).then((value) => {
      if (value) {
        setLoadingDelete(true);
        service
          .delete(`/api/deep-read/rings/${ringId}`)
          .then(() => {
            setRingsByBook((prev) => {
              const next = { ...prev };
              next[bookId] = (next[bookId] || []).filter((r) => r.id !== ringId);
              return next;
            });
            setBooks((prev) =>
              prev.map((b) =>
                b.id === bookId ? { ...b, rings_count: Math.max((b.rings_count || 1) - 1, 0) } : b
              )
            );
            setLoadingDelete(false);
          })
          .catch(() => setLoadingDelete(false));
      }
    });
  };

  useEffect(() => {
    dispatch(
      setInterface({
        active: "Deep Read",
        pageTitle: "Daftar Buku",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [id]);

  return (
    <Fragment>
      <Helmet>
        <title>Buku</title>
      </Helmet>
      <Card className="flex flex-col items-center">
        <div className="flex flex-col items-center">
          <img
            src={
              deepRead?.cover
                ? process.env.REACT_APP_BASE_URL + deepRead.cover
                : `${process.env.REACT_APP_BASE_URL}/no-image.png`
            }
            className="w-32 h-32 object-cover"
            alt="Cover"
          />
          <div className="flex-1 mt-5 text-center">
            <h1 className="font-bold text-base lg:text-lg">
              {deepRead?.title || "Memuat..."}
            </h1>
            <div className="flex flex-wrap mt-1 justify-center text-sm">
              {deepRead?.books_count || 0} Buku
            </div>
          </div>
        </div>
      </Card>
      <Card className="mt-5">
        <div className="flex items-center mb-8">
          <h3 className="text-lg lg:text-xl font-bold monda flex-1">Daftar Buku</h3>
          {saveArranged ? (
            <div>
              <button
                type="button"
                onClick={() => {
                  setSaveArranged(false);
                  setBooks(deepRead?.books || []);
                }}
                className="px-5"
              >
                Batal
              </button>
              <Button type="button" onClick={saveArrangedBooks}>
                Simpan
              </Button>
            </div>
          ) : (
            <Button
              link
              to={`/deep-read/${id}/book/add`}
              color="green"
              noFixedHeight
            >
              <FiPlus className="mr-1" /> Tambah Buku
            </Button>
          )}
        </div>
        {loading ? (
          <div className="h-40 flex justify-center items-center">
            <Loader className="bg-black-100" />
          </div>
        ) : books?.length ? (
          <ul className="flex flex-col gap-5">
            {books.map((book, index) => {
              const isOpen = expanded[book.id];
              const rings = ringsByBook[book.id] || [];
              return (
                <li key={book.id}>
                  <div className="flex items-start relative">
                    {isOpen && rings.length ? (
                      <div
                        className="bg-white-300 absolute"
                        style={{ left: 12, top: 24, bottom: -20, width: 1 }}
                      />
                    ) : null}
                    <div>
                      <button
                        type="button"
                        onClick={() => toggleExpand(book.id)}
                        className="mr-4 w-6 h-6 rounded-sm flex items-center justify-center bg-white-200"
                        title={isOpen ? "Tutup" : "Buka"}
                      >
                        {isOpen ? <FiMinus className="w-4 h-4" /> : <FiPlus className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex-1">
                      <div
                        className="font-bold text-base flex items-center gap-2 flex-wrap cursor-pointer"
                        onClick={() => toggleExpand(book.id)}
                      >
                        <span>{book.title}</span>
                        <span className="text-sm text-black-300">({book.rings_count || 0} Ring)</span>
                      </div>
                      <div className="flex flex-wrap">
                        <Link
                          to={`/deep-read/${id}/book/${book.id}/edit`}
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 flex items-center mr-2 mt-1"
                          title="Edit Buku"
                        >
                          <FiEdit2 />
                        </Link>
                        <Link
                          to={`/deep-read/${id}/book/${book.id}/ring/add`}
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-green-200 hover:border-green-300 hover:text-white-100 flex items-center mr-2 mt-1"
                          title="Tambah Ring"
                        >
                          <FiPlus />
                        </Link>
                        <Link
                          to={`/deep-read/${id}/book/${book.id}/ring`}
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 flex items-center mr-2 mt-1"
                          title="Kelola Ring"
                        >
                          <FiList />
                        </Link>
                        <button
                          type="button"
                          onClick={() => onDelete(book.id)}
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-red-200 hover:border-red-300 hover:text-white-100 flex items-center mr-2 mt-1"
                          title="Hapus Buku"
                        >
                          <FiTrash />
                        </button>
                        <div className="flex mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (index > 0) changePosition("up", index);
                            }}
                            className="p-1.5 rounded-l-sm border border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 flex items-center"
                            title="Urutkan ke atas"
                          >
                            <FiArrowUp />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (index < books.length - 1) changePosition("down", index);
                            }}
                            className="p-1.5 rounded-r-sm border border-l-0 border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 flex items-center"
                            title="Urutkan ke bawah"
                          >
                            <FiArrowDown />
                          </button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 flex flex-col gap-2">
                          {rings.length ? (
                            rings.map((ring, idx) => (
                              <div key={ring.id} className="flex items-center gap-2 relative pl-2">
                                <div
                                  className="bg-white-300 absolute"
                                  style={{ left: -28, top: 14, width: 28, height: 1 }}
                                />
                                <div className="font-bold flex-1">{ring.title}</div>
                                <div className="flex flex-wrap">
                                  <Link
                                    to={`/deep-read/ring/${ring.id}`}
                                    className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 flex items-center mr-2 mt-1"
                                    title="Edit Ring"
                                  >
                                    <FiEdit2 />
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => deleteRing(book.id, ring.id)}
                                    className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-red-200 hover:border-red-300 hover:text-white-100 flex items-center mr-2 mt-1"
                                    title="Hapus Ring"
                                  >
                                    <FiTrash />
                                  </button>
                                  <div className="flex mt-1">
                                    <button
                                      type="button"
                                      onClick={() => reorderRing(book.id, "up", idx)}
                                      disabled={idx === 0}
                                      className="p-1.5 rounded-l-sm border border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                      title="Urutkan ke atas"
                                    >
                                      <FiArrowUp />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => reorderRing(book.id, "down", idx)}
                                      disabled={idx === rings.length - 1}
                                      className="p-1.5 rounded-r-sm border border-l-0 border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                      title="Urutkan ke bawah"
                                    >
                                      <FiArrowDown />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-black-300">Belum ada ring</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center py-10">Belum ada buku</div>
        )}
      </Card>
      {(loadingDelete || savingOrder) && <BigLoader />}
    </Fragment>
  );
}
